import { NgForOf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  WritableSignal,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';

import { tap } from 'rxjs';

import { FinEnvironmentService } from '@finverity/platform-app/core/services/environment.service';

import { ApiStatusService } from '../../data-access/api-status.service';
import { ServiceHealth } from '../../models/service.model';
import { ApiStatusCardComponent } from '../api-status-card/api-status-card.component';

@Component({
  selector: 'fin-api-status-page',
  standalone: true,
  imports: [ApiStatusCardComponent, NgForOf, FormsModule],
  templateUrl: './api-status-page.component.html',
  styleUrls: ['./api-status-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ApiStatusPageComponent {
  private readonly environmentService = inject(FinEnvironmentService);
  private readonly apiStatusService = inject(ApiStatusService);
  private readonly destroyRef = inject(DestroyRef);

  readonly logoSrc = this.environmentService.platform.logo.default.src;
  readonly currentYear = new Date().getFullYear();

  filterQuery = signal('');

  services: WritableSignal<ServiceHealth[]> = signal(this.apiStatusService.getDefaultServices());

  filteredServices = computed(() =>
    this.services().filter(service => service?.name?.toLowerCase().includes(this.filterQuery().toLowerCase()))
  );

  constructor() {
    this.startFetchingStatuses();
  }

  private startFetchingStatuses(): void {
    this.apiStatusService
      .fetchGraphQLStatuses()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(graphqlServices => this.updateServices(graphqlServices))
      )
      .subscribe();
    this.apiStatusService
      .fetchSSOStatus()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(ssoService => this.updateServices([ssoService] as ServiceHealth[]))
      )
      .subscribe();
  }

  private updateServices(newServices: ServiceHealth[]): void {
    const updatedServices = [
      ...this.services().filter(s => !newServices.some(ns => ns.name === s.name)),
      ...newServices,
    ];
    this.services.set(updatedServices);
  }
}
